import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip, ProgressBar, BigStat, StatRow, Icon, Sparkline } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import DiamondAmount from '../shared/DiamondAmount';
import { formatDiamonds, formatInt, isNum } from '../shared/format';

/**
 * Module 4 — single-metric creator sub-views (mockup 1:1).
 *
 * Mirrors the M3 `TierMetricRow` precedent: the creator detail page exposes a
 * `?view=` pill selector and renders one of these focused artboards instead of
 * the stacked overview. Each view maps to a mockup artboard:
 *   - diamond  → A4.2 Diamond detail (BigStat hero + progress + runrate/EoM/gap)
 *   - pk       → A4.3 PK detail (2-col count/diamonds + efficiency)
 *   - rankup   → A4.4 Rank-up / Maintain (current → forecast + 7D/15H + need)
 *   - active   → A4.5 Activeness (level + days/hours + gap to L+1)
 *
 * Financial fields arrive decrypted from the parent page (tcn-decrypt-creator).
 * While `decrypting`/no data, encrypted-aware widgets render the "—" placeholder.
 */

const alertAccent = (alert) =>
  alert === 'critical' ? 'red' : alert === 'at_risk' ? 'amber' : null;

const alertColor = (alert) =>
  alert === 'critical' ? TCN.red : alert === 'at_risk' ? TCN.amber : TCN.green;

const SubLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: TCN.textMute,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </div>
);

const CreatorMetricView = ({
  view,
  creator,
  decrypted,
  decrypting = false,
  usdRate,
  effSeries = [],
}) => {
  const { t } = useTranslation();
  if (!creator) return null;

  const dec = decrypted || {};
  const showEncrypted = decrypting || !decrypted;
  const alert = creator.alert_status || 'none';

  /* ---------------- A4.2 Diamonds ---------------- */
  if (view === 'diamond') {
    const mtd = dec.diamonds_mtd;
    const target = dec.creator_monthly_target;
    const eom = dec.eom_expected;
    const completion =
      isNum(mtd) && isNum(target) && target > 0 ? Math.round((mtd / target) * 100) : null;
    const gap = isNum(mtd) && isNum(target) ? mtd - target : null;
    const behind = isNum(eom) && isNum(target) && eom < target;
    const eomPct =
      isNum(eom) && isNum(target) && target > 0 ? Math.round((eom / target) * 100) : null;

    return (
      <Card accent={alertAccent(alert)}>
        {alert !== 'none' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Icon name="warn" size={14} color={alertColor(alert)} />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: alertColor(alert),
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t(`tcn.m4.alert.${alert}`, alert.replace('_', ' '))}
            </span>
          </div>
        )}
        <BigStat
          label={t('tcn.m4.detail.mtdActual', 'MTD actual')}
          value={showEncrypted ? '—' : formatDiamonds(mtd)}
          unit="◆"
          sub={
            showEncrypted
              ? undefined
              : t('tcn.m4.view.diamond.ofTarget', 'of {{target}} target', {
                  target: formatDiamonds(target),
                })
          }
        />
        <div style={{ marginTop: 14 }}>
          <ProgressBar
            value={isNum(mtd) ? mtd : 0}
            target={isNum(target) && target > 0 ? target : 100}
            /* Colour by pace-health (alert_status), not raw MTD ratio — a mid-month
               creator on track to exceed target should not read red. */
            color={alert === 'critical' ? 'red' : alert === 'at_risk' ? 'amber' : 'green'}
            showCap
          />
          {completion != null && (
            <div style={{ marginTop: 6, fontSize: 11, color: behind ? TCN.amber : TCN.textDim }}>
              {t('tcn.m4.view.diamond.complete', '{{pct}}% complete', { pct: completion })}
              {behind && eomPct != null && (
                <>
                  {' · '}
                  {t('tcn.m4.view.diamond.eomTracking', 'EoM tracking {{pct}}%', { pct: eomPct })}
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${TCN.border}` }}>
          <StatRow
            label={t('tcn.m4.view.diamond.lastMonth', 'Last month')}
            value={<DiamondAmount value={dec.diamonds_last_month} encrypted={showEncrypted} />}
            mono
          />
          <StatRow
            label={t('tcn.m4.detail.runrate', 'Runrate (daily)')}
            value={<DiamondAmount value={dec.runrate_daily} encrypted={showEncrypted} />}
            mono
            divider
          />
          <StatRow
            label={t('tcn.m4.detail.eomExpected', 'EoM expected')}
            value={<DiamondAmount value={dec.eom_expected} encrypted={showEncrypted} usdRate={usdRate} />}
            mono
            divider
          />
          <StatRow
            label={t('tcn.m4.view.diamond.gapToTarget', 'Gap to target')}
            value={
              showEncrypted || gap == null ? (
                '—'
              ) : (
                <span dir="ltr" style={{ color: gap < 0 ? TCN.red : TCN.green }}>
                  {gap < 0 ? '−' : '+'}
                  {formatDiamonds(Math.abs(gap))}
                </span>
              )
            }
            mono
            divider
          />
        </div>
      </Card>
    );
  }

  /* ---------------- A4.3 PK ---------------- */
  if (view === 'pk') {
    const eff = dec.pk_efficiency;
    return (
      <Card accent="magenta">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Icon name="fire" size={16} color={TCN.magenta} />
          <span style={{ fontSize: 13, fontWeight: 700, color: TCN.text }}>
            {t('tcn.m4.view.pk.title', 'PK performance')}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <SubLabel>{t('tcn.m4.detail.pkCountMtd', 'PK count (MTD)')}</SubLabel>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                marginTop: 4,
                color: TCN.text,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: TCN.fontMono,
              }}
            >
              {creator.pk_count_mtd != null ? formatInt(creator.pk_count_mtd) : '—'}
            </div>
          </div>
          <div>
            <SubLabel>{t('tcn.m4.detail.diamondsFromPk', 'Diamonds from PK')}</SubLabel>
            <div style={{ marginTop: 4 }}>
              <DiamondAmount
                value={dec.diamonds_from_pk}
                encrypted={showEncrypted}
                color={TCN.diamond}
                style={{ fontSize: 28, fontWeight: 700 }}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 18,
            padding: 14,
            background: TCN.surface2,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <SubLabel>{t('tcn.m4.detail.pkEfficiency', 'PK efficiency')}</SubLabel>
            <div
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: TCN.magenta,
                marginTop: 4,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: TCN.fontMono,
              }}
            >
              {showEncrypted || !isNum(eff) ? '—' : formatInt(Math.round(eff))}
            </div>
            <div style={{ fontSize: 11, color: TCN.textDim, marginTop: 2 }}>
              {t('tcn.m4.view.pk.perPk', 'diamonds per PK')}
            </div>
          </div>
          {/* A4.3 — efficiency trend sparkline. Renders the recent daily series
              when available (populated once daily history is fed); a subtle
              dashed baseline otherwise. RTL-mirrored so the trend reads in
              the natural reading direction. */}
          <Sparkline
            data={Array.isArray(effSeries) ? effSeries : []}
            color="magenta"
            width={84}
            height={34}
            strokeWidth={2}
            style={{ flexShrink: 0 }}
          />
        </div>
      </Card>
    );
  }

  /* ---------------- A4.4 Rank-up / Maintain ---------------- */
  if (view === 'rankup') {
    const cur = creator.tier_current;
    const last = creator.tier_last_month;
    const fc = creator.forecasted_tier;
    const elig = !!creator.rank_up_eligibility_7d15h;
    const isMaintain = isNum(fc) && isNum(last) && fc === last;
    const isDemote = isNum(fc) && isNum(last) && fc < last;
    const color = isDemote ? TCN.red : isMaintain ? TCN.cyan : TCN.green;
    const typeLabel = isDemote
      ? t('tcn.m4.view.rankup.demote', 'Demote risk')
      : isMaintain
        ? t('tcn.m4.view.rankup.maintain', 'Maintain')
        : t('tcn.m4.view.rankup.rankUp', 'Rank up');
    // For maintains the relevant gap is maintain_*; otherwise rank-up need / overshoot.
    const need = isMaintain ? dec.maintain_diamonds_needed : dec.rank_up_diamonds_needed;
    const overshoot = isMaintain
      ? dec.maintain_diamonds_overshoot
      : dec.rank_up_diamonds_overshoot;

    return (
      <Card accent={isDemote ? 'red' : null}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            {/* Tier progression stays LTR so the → reads "T6 → T7" in every locale. */}
            <div dir="ltr" style={{ fontSize: 13, fontWeight: 700, color: TCN.text, textAlign: 'start' }}>
              {isNum(cur) ? `T${cur}` : '—'}
              <span style={{ color: TCN.textMute, fontWeight: 600 }}>
                {' '}
                {t('tcn.m4.view.rankup.forecast', '→ forecast')}{' '}
              </span>
              <span style={{ color }}>{isNum(fc) ? `T${fc}` : '—'}</span>
            </div>
          </div>
          <Chip size="sm" color={isDemote ? 'red' : isMaintain ? 'cyan' : 'green'} solid>
            {typeLabel}
          </Chip>
        </div>
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${TCN.border}`,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
          }}
        >
          <div>
            <SubLabel>{t('tcn.m4.detail.eligibility7d15h', 'Eligibility 7D / 15H')}</SubLabel>
            <div
              style={{
                fontSize: 13,
                marginTop: 4,
                fontWeight: 700,
                color: elig ? TCN.green : TCN.amber,
              }}
            >
              {elig
                ? t('tcn.m4.view.rankup.met', '✓ Met')
                : t('tcn.m4.view.rankup.notMet', '✗ Not met')}
            </div>
          </div>
          <div>
            <SubLabel>{t('tcn.m4.view.rankup.need', 'Need')}</SubLabel>
            <div style={{ marginTop: 4, fontWeight: 700 }}>
              {isMaintain && isNum(need) && need <= 0 ? (
                <span style={{ fontSize: 13, color: TCN.green }}>
                  {t('tcn.m4.view.rankup.alreadyMaintained', 'Already maintained')}
                </span>
              ) : showEncrypted ? (
                <DiamondAmount value={null} encrypted />
              ) : isNum(need) && need > 0 ? (
                <DiamondAmount value={need} color={TCN.text} />
              ) : isNum(overshoot) && overshoot > 0 ? (
                <span dir="ltr" style={{ fontSize: 13, color: TCN.green }}>
                  +{formatDiamonds(overshoot)} {t('tcn.m4.view.rankup.over', 'over')}
                </span>
              ) : (
                '—'
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  /* ---------------- A4.5 Activeness ---------------- */
  if (view === 'active') {
    const lvl = creator.activeness_level;
    const hasLevel = isNum(lvl) && lvl >= 1;
    const isMax = creator.is_max_level;
    const lvlColor = !hasLevel
      ? TCN.textMute
      : lvl >= 4
        ? TCN.green
        : lvl === 3
          ? TCN.cyan
          : lvl === 2
            ? TCN.amber
            : TCN.red;
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              background: `${lvlColor}1c`,
              border: `1px solid ${lvlColor}55`,
            }}
          >
            <span style={{ fontSize: 22, fontWeight: 800, color: lvlColor }}>
              {hasLevel ? `L${lvl}` : '—'}
            </span>
            {isMax && (
              <span style={{ fontSize: 9, fontWeight: 700, color: lvlColor }}>MAX</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TCN.text }}>
              {hasLevel
                ? t('tcn.m4.view.active.level', 'Activeness L{{level}}', { level: lvl })
                : t('tcn.m4.view.active.awaiting', 'Awaiting data')}
            </div>
            <div style={{ fontSize: 11, color: TCN.textDim, marginTop: 2 }}>
              {creator.live_days_mtd != null
                ? `${formatInt(creator.live_days_mtd)}${t('tcn.m4.view.active.daysShort', 'd')}`
                : '—'}
              {' · '}
              {isNum(creator.live_hours_mtd)
                ? `${creator.live_hours_mtd.toFixed(1)}${t('tcn.m4.view.active.hoursShort', 'h')}`
                : '—'}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${TCN.border}` }}>
          <StatRow
            label={t('tcn.m4.detail.liveDaysMtd', 'LIVE days (MTD)')}
            value={creator.live_days_mtd != null ? formatInt(creator.live_days_mtd) : '—'}
            hint={
              creator.live_days_mtd == null
                ? t('tcn.m4.detail.awaitingDailyHistory', 'Awaiting daily history')
                : undefined
            }
            mono
          />
          <StatRow
            label={t('tcn.m4.detail.liveHoursMtd', 'LIVE hours (MTD)')}
            value={isNum(creator.live_hours_mtd) ? `${creator.live_hours_mtd.toFixed(1)}h` : '—'}
            mono
            divider
          />
          {!isMax && hasLevel && (
            <StatRow
              label={t('tcn.m4.detail.gapToNextLevel', 'Gap to L{{level}}', {
                level: creator.gap_to_next_level_target ?? '?',
              })}
              value={
                <span style={{ fontSize: 12, color: TCN.textDim }}>
                  {t('tcn.m4.detail.gapNeed', 'Need {{diamonds}} + {{days}}d + {{hours}}h', {
                    diamonds:
                      showEncrypted || dec.gap_to_next_level_diamonds == null
                        ? '— ◆'
                        : `${formatDiamonds(dec.gap_to_next_level_diamonds)} ◆`,
                    days: creator.gap_to_next_level_days ?? 0,
                    hours: isNum(creator.gap_to_next_level_hours)
                      ? creator.gap_to_next_level_hours.toFixed(1)
                      : 0,
                  })}
                </span>
              }
              divider
            />
          )}
        </div>
      </Card>
    );
  }

  return null;
};

export default CreatorMetricView;
